<script lang="ts" setup>
import { ref, computed } from 'vue';

const props = defineProps<{
    modelValue: number | string | boolean | undefined | 'Yes' | 'No',
    min: number | undefined,
    max: number | undefined,
    options: string[] | string | undefined,
    id?: string,
    checkGuid?: string
}>();
const val = ref<Date | undefined>(undefined);
const emit = defineEmits(["update", "next"]);

// Mirror to localStorage (synchronous, commits immediately) so the moment this message was
// dismissed survives the app being backgrounded/killed before the async IndexedDB write lands.
const backupKey = () => `cm_timestamp_${props.checkGuid}_${props.id}`;
const readBackup = (): Date | undefined => {
    if (!props.checkGuid || !props.id) return undefined;
    try {
        const raw = localStorage.getItem(backupKey());
        return raw ? new Date(raw) : undefined;
    } catch {
        return undefined;
    }
}
const writeBackup = (d: Date) => {
    if (!props.checkGuid || !props.id) return;
    try {
        localStorage.setItem(backupKey(), d.toISOString());
    } catch {
        // storage unavailable/full - nothing more we can do
    }
}
const clearBackup = () => {
    if (!props.checkGuid || !props.id) return;
    try {
        localStorage.removeItem(backupKey());
    } catch {
        // ignore
    }
}

const load = (value: string | number | boolean | undefined) => {
    if (value) {
        // Already has a recorded time (e.g. re-synced during final submit) - keep it.
        val.value = new Date(value as string);
        clearBackup();
    }
}
const validate = () :  'under' | 'over' | 'invalid' | 'ok' | 'ignore' | 'error' => {
    return 'ok'
}
// The saved time from the bound task value - load() isn't called when the user lands on this
// page via "Previous", so don't overwrite an already-recorded time.
const savedValue = (): Date | undefined => {
    if (!props.modelValue || typeof props.modelValue === 'boolean') return undefined;
    const d = new Date(props.modelValue as string);
    return isNaN(d.getTime()) ? undefined : d;
}
const value = () => {
   if (val.value == undefined) {
       const saved = savedValue();
       if (saved) return val.value = saved;
       // First time this is read - the user is dismissing the message right now.
       val.value = readBackup() ?? new Date();
       writeBackup(val.value);
   }
   return val.value;
}
const title = computed(() => {
    return props.options != undefined && props.options.length > 0 ? props.options[0] : ""
})
const msg = computed(() => {
    return props.options != undefined && props.options.length > 1 ? props.options[1].replace(/\n/gmi, "<br />") : ""
})

defineExpose({ validate, load, value });

</script>

<template>
    <div class="message">
        <el-alert type="info" center :title="title" >
            <div  v-html="msg" style="font-size: 1.5em"></div>
        </el-alert>

    </div>
</template>

<style>
    .message{
        height: 100%;
    }

    .el-alert__title.with-description {
        font-size: 1.7em;
    }
</style>